import { CheckCircle2, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Modal } from "@/components/Modal";

import type { Connectivity } from "./connectivity";

interface Row {
  key: string;
  ok: boolean;
}

/** What each state allows — the same table as `offlineRules` (design/05 §4), in plain words. */
export function detailRows(state: Connectivity): Row[] {
  const short = state !== "offline_long";
  return [
    { key: "sell_sealed", ok: true },
    { key: short ? "sell_single_fresh" : "sell_single_none", ok: short },
    { key: "sell_listed", ok: false },
    { key: "cash", ok: true },
    { key: "card", ok: false },
    { key: "credit", ok: false },
    { key: "discount", ok: false },
    { key: "tax_free", ok: false },
    { key: "returns", ok: false },
    { key: "buylist", ok: false },
    { key: "close", ok: false },
    { key: "receipt", ok: true },
  ];
}

/** 「詳細」 of the status bar: what can be done now and what waits for the connection. */
export function OfflineDetailsSheet({
  open,
  onClose,
  state,
}: {
  open: boolean;
  onClose: () => void;
  state: Connectivity;
}) {
  const { t } = useTranslation();
  const rows = detailRows(state);
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={state === "offline_long" ? t("offline.status_long") : t("offline.status_short")}
      description={t("offline.details_intro")}
      size="lg"
      footer={
        <button
          type="button"
          onClick={onClose}
          className="min-h-pos-touch rounded-md border px-6 text-lg"
        >
          {t("app.close")}
        </button>
      }
    >
      <ul className="grid gap-2 sm:grid-cols-2">
        {rows.map((row) => (
          <li
            key={row.key}
            className="flex min-h-12 items-center gap-3 rounded-md border px-3 py-2 text-base"
          >
            {row.ok ? (
              <CheckCircle2 aria-hidden className="size-6 shrink-0 text-pos-ok" />
            ) : (
              <XCircle aria-hidden className="size-6 shrink-0 text-pos-problem" />
            )}
            <span>
              <span className="sr-only">{row.ok ? t("offline.can") : t("offline.cannot")}: </span>
              {t(`offline.row_${row.key}`)}
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-4 text-sm text-muted-foreground">{t("offline.details_after")}</p>
    </Modal>
  );
}
